/**
 * src/components/ui/AppSplashScreen.tsx
 * Ultra-Premium Apple iOS 18 Minimal Logo Opening Launch Animation
 * Features: Fluid spring scale-in, specular breathing aura, subtitle track-in,
 * and seamless fade-out into the main application.
 */

import React, { useEffect, useState } from 'react';
import { useTheme } from '../../context/ThemeContext';
import { haptic } from '../../utils/audioHaptics';

interface AppSplashScreenProps {
  onFinished: () => void;
  forceDuration?: number;
}

export const AppSplashScreen: React.FC<AppSplashScreenProps> = ({ 
  onFinished,
  forceDuration = 1500 
}) => {
  const { theme, activeLogo } = useTheme();
  const [stage, setStage] = useState<'enter' | 'settled' | 'exit'>('enter');

  useEffect(() => {
    // 1. Enter state on mount
    const t1 = setTimeout(() => {
      setStage('settled');
    }, 100);

    // 2. Trigger exit fade-out slightly before finishing
    const t2 = setTimeout(() => {
      setStage('exit');
      haptic.light();
    }, forceDuration - 350);

    // 3. Complete and unmount
    const t3 = setTimeout(() => {
      onFinished();
    }, forceDuration);

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
    };
  }, [forceDuration, onFinished]);

  const isDark = theme === 'dark';

  return (
    <div
      onClick={onFinished}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 99999,
        background: isDark ? '#05070a' : '#f8fafc',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        opacity: stage === 'exit' ? 0 : 1,
        transform: stage === 'exit' ? 'scale(1.04)' : 'scale(1)',
        transition: 'opacity 0.38s cubic-bezier(0.4, 0, 0.2, 1), transform 0.38s cubic-bezier(0.4, 0, 0.2, 1)',
        pointerEvents: stage === 'exit' ? 'none' : 'auto',
        userSelect: 'none',
        cursor: 'pointer'
      }}
    >
      {/* Ambient background glowing radial bloom */}
      <div
        style={{
          position: 'absolute',
          width: '380px',
          height: '380px',
          borderRadius: '50%',
          background: isDark 
            ? 'radial-gradient(circle, rgba(10, 132, 255, 0.22) 0%, rgba(0, 0, 0, 0) 70%)'
            : 'radial-gradient(circle, rgba(10, 132, 255, 0.15) 0%, rgba(255, 255, 255, 0) 70%)',
          filter: 'blur(40px)',
          pointerEvents: 'none',
          animation: 'pulseGlow 2.4s infinite ease-in-out'
        }}
      />

      {/* Center 3D Logo Presentation */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '18px',
          position: 'relative',
          zIndex: 2,
          transform: stage === 'enter' ? 'scale(0.82) translateY(12px)' : 'scale(1) translateY(0)',
          opacity: stage === 'enter' ? 0 : 1,
          transition: 'transform 0.65s cubic-bezier(0.16, 1, 0.3, 1), opacity 0.45s ease-out'
        }}
      >
        {/* 3D App Icon Container */}
        <div
          style={{
            width: '104px',
            height: '104px',
            borderRadius: '28px',
            overflow: 'hidden',
            boxShadow: isDark
              ? '0 20px 50px rgba(10, 132, 255, 0.35), 0 0 0 1px rgba(255, 255, 255, 0.15)'
              : '0 20px 50px rgba(0, 0, 0, 0.12), 0 0 0 1px rgba(0, 0, 0, 0.06)',
            background: isDark ? '#0b101b' : '#ffffff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative'
          }}
        >
          <img
            src={activeLogo}
            alt="CKAudit Logo"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover'
            }}
          />
        </div>

        {/* Brand Text & Tagline */}
        <div style={{ textAlign: 'center' }}>
          <div
            style={{
              fontSize: '1.65rem',
              fontWeight: 800,
              letterSpacing: '-0.03em',
              color: isDark ? '#ffffff' : '#0f172a',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px'
            }}
          >
            <span>CKAudit</span>
          </div>

          <div
            style={{
              fontSize: '0.75rem',
              fontWeight: 700,
              letterSpacing: '0.22em',
              textTransform: 'uppercase',
              color: isDark ? 'rgba(255, 255, 255, 0.45)' : 'rgba(15, 23, 42, 0.45)',
              marginTop: '4px'
            }}
          >
            BOOK • SHARE • SETTLE
          </div>
        </div>

        {/* Minimal Progress Line Indicator */}
        <div
          style={{
            width: '64px',
            height: '3px',
            borderRadius: '99px',
            background: isDark ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.08)',
            overflow: 'hidden',
            marginTop: '8px'
          }}
        >
          <div
            style={{
              height: '100%',
              borderRadius: '99px',
              background: 'linear-gradient(90deg, #0a84ff 0%, #30d158 100%)',
              width: stage === 'settled' || stage === 'exit' ? '100%' : '0%',
              transition: 'width 1.1s cubic-bezier(0.16, 1, 0.3, 1)'
            }}
          />
        </div>
      </div>
    </div>
  );
};
